import { uiPreferences } from "../ui/preferences";

const KNOTS_PER_METRE_PER_SECOND = 1.943_844_492_440_6;
const EARTH_RADIUS_KM = 6371.0088;

export interface WindLocation {
  longitude: number;
  latitude: number;
}

export interface WindReading extends WindLocation {
  stationId: string;
  stationName: string;
  speedKnots: number;
  direction: number;
  observedAt: number;
}

export interface InterpolatedWind extends WindLocation {
  speedKnots: number;
  direction: number;
  observedAt: number;
  nearestStation: string;
  nearestDistanceKm: number;
  stationCount: number;
}

export interface ForecastWindSample {
  speedKnots: number;
  direction: number;
  weatherCode: number;
  precipitationMm: number;
  isDay: boolean;
}

export interface ForecastWindSeries extends WindLocation {
  samples: Array<ForecastWindSample | null>;
}

interface SmhiValue {
  date?: unknown;
  value?: unknown;
}

interface SmhiStation {
  key?: unknown;
  name?: unknown;
  latitude?: unknown;
  longitude?: unknown;
  value?: unknown;
}

interface SmhiStationResponse {
  station?: unknown;
}

interface OpenMeteoHourly {
  time?: unknown;
  wind_speed_10m?: unknown;
  wind_direction_10m?: unknown;
  weather_code?: unknown;
  precipitation?: unknown;
  is_day?: unknown;
}

interface OpenMeteoResponse {
  hourly?: OpenMeteoHourly;
  error?: unknown;
  reason?: unknown;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value !== "number" && (typeof value !== "string" || !value.trim())) {
    return null;
  }
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

function latestSmhiValue(station: SmhiStation): { value: number; date: number } | null {
  if (!Array.isArray(station.value)) return null;
  for (let index = station.value.length - 1; index >= 0; index--) {
    const candidate = station.value[index] as SmhiValue;
    const value = finiteNumber(candidate.value);
    const date = finiteNumber(candidate.date);
    if (value !== null && date !== null) return { value, date };
  }
  return null;
}

async function fetchSmhiParameter(parameter: 3 | 4, signal?: AbortSignal): Promise<SmhiStation[]> {
  const baseUrl = uiPreferences().smhiBaseUrl;
  const response = await fetch(
    `${baseUrl}/parameter/${parameter}/station-set/all/period/latest-hour/data.json`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(`SMHI wind observations could not be loaded (${response.status}).`);
  }
  const payload = (await response.json()) as SmhiStationResponse;
  if (!Array.isArray(payload.station)) {
    throw new Error("SMHI returned an unexpected wind observation response.");
  }
  return payload.station as SmhiStation[];
}

/** Joins SMHI's separate hourly direction and speed station sets. */
export async function fetchLatestWindReadings(signal?: AbortSignal): Promise<WindReading[]> {
  const [directions, speeds] = await Promise.all([
    fetchSmhiParameter(3, signal),
    fetchSmhiParameter(4, signal),
  ]);
  const speedByStation = new Map(
    speeds
      .map((station): [string, SmhiStation] | null => {
        const key = typeof station.key === "string" ? station.key : null;
        return key ? [key, station] : null;
      })
      .filter((entry): entry is [string, SmhiStation] => entry !== null),
  );
  const readings: WindReading[] = [];
  for (const directionStation of directions) {
    const stationId =
      typeof directionStation.key === "string" ? directionStation.key : null;
    const speedStation = stationId ? speedByStation.get(stationId) : null;
    const directionValue = latestSmhiValue(directionStation);
    const speedValue = speedStation ? latestSmhiValue(speedStation) : null;
    const latitude = finiteNumber(directionStation.latitude);
    const longitude = finiteNumber(directionStation.longitude);
    if (
      !stationId ||
      !directionValue ||
      !speedValue ||
      latitude === null ||
      longitude === null ||
      directionValue.value < 0 ||
      directionValue.value > 360 ||
      speedValue.value < 0
    ) {
      continue;
    }
    readings.push({
      stationId,
      stationName:
        typeof directionStation.name === "string"
          ? directionStation.name
          : `Station ${stationId}`,
      latitude,
      longitude,
      speedKnots: speedValue.value * KNOTS_PER_METRE_PER_SECOND,
      direction: directionValue.value % 360,
      observedAt: Math.min(directionValue.date, speedValue.date),
    });
  }
  if (!readings.length) throw new Error("SMHI returned no current wind observations.");
  return readings;
}

function radians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function distanceKm(a: WindLocation, b: WindLocation): number {
  const latitudeDelta = radians(b.latitude - a.latitude);
  const longitudeDelta = radians(b.longitude - a.longitude);
  const latitudeA = radians(a.latitude);
  const latitudeB = radians(b.latitude);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(latitudeA) * Math.cos(latitudeB) * Math.sin(longitudeDelta / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

/**
 * Interpolates speed and the circular direction vector from nearby stations.
 * A one-kilometre floor prevents a station at the exact map point from
 * producing an infinite weight.
 */
export function interpolateWind(
  readings: WindReading[],
  location: WindLocation,
  stationLimit = 4,
): InterpolatedWind | null {
  const nearest = readings
    .map((reading) => ({ reading, distance: distanceKm(reading, location) }))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, stationLimit);
  if (!nearest.length) return null;

  let totalWeight = 0;
  let speed = 0;
  let directionEast = 0;
  let directionNorth = 0;
  for (const { reading, distance } of nearest) {
    const weight = 1 / Math.max(1, distance) ** 2;
    totalWeight += weight;
    speed += reading.speedKnots * weight;
    directionEast += Math.sin(radians(reading.direction)) * weight;
    directionNorth += Math.cos(radians(reading.direction)) * weight;
  }
  const direction =
    (Math.atan2(directionEast, directionNorth) * 180) / Math.PI;
  return {
    ...location,
    speedKnots: speed / totalWeight,
    direction: (direction + 360) % 360,
    observedAt: Math.min(...nearest.map(({ reading }) => reading.observedAt)),
    nearestStation: nearest[0].reading.stationName,
    nearestDistanceKm: nearest[0].distance,
    stationCount: nearest.length,
  };
}

function numberArray(value: unknown): number[] | null {
  if (!Array.isArray(value)) return null;
  const result = value.map(finiteNumber);
  return result.every((item): item is number => item !== null) ? result : null;
}

function parseForecastResponse(
  payload: OpenMeteoResponse,
  location: WindLocation,
  date: string,
): ForecastWindSeries {
  if (payload.error) {
    throw new Error(
      typeof payload.reason === "string" ? payload.reason : "The wind forecast was rejected.",
    );
  }
  const times =
    Array.isArray(payload.hourly?.time) &&
    payload.hourly.time.every((time): time is string => typeof time === "string")
      ? payload.hourly.time
      : null;
  const speeds = numberArray(payload.hourly?.wind_speed_10m);
  const directions = numberArray(payload.hourly?.wind_direction_10m);
  const weatherCodes = numberArray(payload.hourly?.weather_code);
  const precipitation = numberArray(payload.hourly?.precipitation);
  const dayFlags = numberArray(payload.hourly?.is_day);
  if (!times || !speeds || !directions || !weatherCodes || !precipitation || !dayFlags) {
    throw new Error("Open-Meteo returned an unexpected wind forecast response.");
  }

  const byTime = new Map(times.map((time, index) => [time, index]));
  const samples = Array.from({ length: 24 }, (_, hour): ForecastWindSample | null => {
    const index = byTime.get(`${date}T${String(hour).padStart(2, "0")}:00`);
    if (
      index === undefined ||
      speeds[index] === undefined ||
      directions[index] === undefined ||
      weatherCodes[index] === undefined ||
      precipitation[index] === undefined ||
      dayFlags[index] === undefined
    ) {
      return null;
    }
    return {
      speedKnots: speeds[index],
      direction: directions[index] % 360,
      weatherCode: weatherCodes[index],
      precipitationMm: precipitation[index],
      isDay: dayFlags[index] === 1,
    };
  });
  return { ...location, samples };
}

/** Loads a full local-calendar day for several DMI grid points in one request. */
export async function fetchForecastWind(
  locations: WindLocation[],
  date: string,
  timeZone: string,
  signal?: AbortSignal,
): Promise<ForecastWindSeries[]> {
  if (!locations.length) return [];
  const parameters = new URLSearchParams({
    latitude: locations.map(({ latitude }) => latitude.toFixed(5)).join(","),
    longitude: locations.map(({ longitude }) => longitude.toFixed(5)).join(","),
    hourly: "wind_speed_10m,wind_direction_10m,weather_code,precipitation,is_day",
    wind_speed_unit: "kn",
    start_date: date,
    end_date: date,
    timezone: timeZone,
    cell_selection: "sea",
    models: "dmi_seamless",
  });
  const url = new URL(uiPreferences().openMeteoUrl, location.origin);
  for (const [key, value] of parameters) url.searchParams.set(key, value);
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`The DMI wind forecast could not be loaded (${response.status}).`);
  }
  const payload = (await response.json()) as OpenMeteoResponse | OpenMeteoResponse[];
  const responses = Array.isArray(payload) ? payload : [payload];
  if (responses.length !== locations.length) {
    throw new Error("Open-Meteo returned an incomplete wind forecast grid.");
  }
  return responses.map((item, index) =>
    parseForecastResponse(item, locations[index], date),
  );
}

export function cardinalDirection(direction: number): string {
  const points = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const normalized = ((direction % 360) + 360) % 360;
  return points[Math.round(normalized / 45) % points.length];
}

export function weatherDescription(
  code: number,
  isDay: boolean,
  precipitationMm: number,
): { icon: string; label: string } {
  if (code >= 95) return { icon: "⛈", label: "Thunderstorms" };
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) {
    return { icon: "❄", label: "Snow" };
  }
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82) || precipitationMm > 0) {
    return {
      icon: "☂",
      label: precipitationMm > 0 ? `Rain ${precipitationMm.toFixed(1)} mm` : "Rain",
    };
  }
  if (code === 45 || code === 48) return { icon: "≋", label: "Fog" };
  if (code === 0) return { icon: isDay ? "☀" : "☾", label: "Clear" };
  if (code <= 3) return { icon: isDay ? "◒" : "☾", label: "Cloudy" };
  return { icon: "•", label: "Weather" };
}
